import * as React from 'react';
import {
  Flex,
  Box,
  Icon,
  Button,
  ButtonGroup,
  Heading,
  Stack,
  Text,
  Spacer,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { ZodIssueCode } from 'zod';

import {
  ABN,
  XeroMyConnectionDetailsResponseConnection,
} from '@zap-onboard/api-client';
import { createToast, removeSpaces } from '../../../../../../helpers';
import { onError } from '../../../../../../libs/errorLib';
import { useAPI } from '../../../../../../services/API';
import { z } from '../../../../../../helpers/schema';
import { track } from '../../../../../../libs/track';

import { inputs } from '@zap-onboard/web-components';
import { AcceptTOCCheckbox } from '../../../../../../components';
import { auth } from '../../../../../../auth';
import { ProfileForm } from '../../../../../Profile/UserProfile/Form';
import { useUpdateUserProfile } from '../../../../../../hooks/data/userProfile/useUpdateUserProfile/useUpdateUserProfile';
import { IconType } from 'react-icons';
import { FiClipboard, FiUser } from 'react-icons/fi';
import { FaRegBuilding } from 'react-icons/fa';

type StepLineProps = {
  steps: {
    icon: IconType;
    active?: boolean;
  }[];
  maxConnectorWidth?: string;
  _container?: Partial<React.ComponentProps<typeof Flex>>;
};

const StepLine = ({
  steps,
  maxConnectorWidth = '20px',
  _container,
}: StepLineProps) => {
  return (
    <Flex w="100%" justify="center" {..._container}>
      <Flex align="center">
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;

          return (
            <Flex key={i} align="center">
              <Flex
                align="center"
                justify="center"
                w="40px"
                h="40px"
                borderRadius="full"
                bg={step.active ? 'blue.500' : 'gray.200'}
                color={step.active ? 'white' : 'gray.600'}
                boxShadow="sm"
                flexShrink={0}
              >
                <Icon as={step.icon} boxSize={5} />
              </Flex>

              {!isLast && (
                <Box
                  mx={3}
                  h="2px"
                  w={maxConnectorWidth}
                  bg="brand.gray"
                  flexShrink={0}
                />
              )}
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

export const SetupBusinessForm = (props: {
  afterSetup?: (businessId: string) => unknown;
  onCancel?: () => unknown;
  xeroOrg?: XeroMyConnectionDetailsResponseConnection;
  /**
   * Passed to prevent a user who is a xero lead marking _another_ business as
   * a lead on their second business registration
   */
  isXeroLead?: boolean;
}) => {
  const { onCancel, afterSetup, xeroOrg, isXeroLead } = props;
  const { useState } = React;
  const api = useAPI();

  const managers = auth.useBusinessManagers();
  const profile = auth.useProfile();

  const needsProfileFields =
    !profile?.primaryContactNumber ||
    !profile?.givenName ||
    !profile.familyName;

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const businessNames = React.useMemo(
    () => managers.map((r) => removeSpaces(r.businessName.toLowerCase())),
    [managers],
  );

  const SetupBusinessFormSchema = z.object({
    businessName: z
      .string()
      .min(4)
      .max(150)
      .superRefine((val, ctx) => {
        const businessName = removeSpaces(val.toLowerCase());

        if (businessNames.includes(businessName)) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: 'A business with this name is already registered',
          });
        }
      }),
    abn: z.valueObject(z.string(), ABN),
  });

  const useFormMethods = useForm<z.infer<typeof SetupBusinessFormSchema>>({
    defaultValues: {
      businessName: xeroOrg?.xeroTenantName ?? '',
      abn: '',
    },
    resolver: z.zodResolver(SetupBusinessFormSchema),
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = useFormMethods;

  const onSubmit = handleSubmit(async ({ businessName, abn }) => {
    const name = businessName.trim();

    await api
      .business()
      .register({
        isXeroLead: isXeroLead ?? false,
        abn,
        name,
      })
      .map(async ({ businessId }) => {
        track.event('Business Registered', {
          businessId,
        });
        if (afterSetup) {
          await afterSetup(businessId);
        }
        await auth.reInit({ currentBusinessId: businessId });
        createToast({
          status: 'success',
          title: 'Business registered',
          description: `We have added ${name} to your profile`,
          position: 'top',
          duration: 6000,
        });
      }, onError);
  });

  const updateProfile = useUpdateUserProfile();
  const profileForm = useForm({
    resolver: z.zodResolver(
      ProfileForm.Schema.pick({
        familyName: true,
        givenName: true,
        primaryContactNumber: true,
      }),
    ),
    defaultValues: ProfileForm.getDefaultValue(profile),
  });

  const steps = (
    <StepLine
      _container={{ p: 6 }}
      maxConnectorWidth="8"
      steps={[
        {
          icon: FiUser,
          active: needsProfileFields,
        },
        {
          icon: FaRegBuilding,
          active: !needsProfileFields,
        },
      ]}
    />
  );

  if (needsProfileFields) {
    return (
      <Stack spacing={2}>
        {steps}
        <Heading variant="section">Complete your profile</Heading>
        <Text pb={6} color="brand.dark-gray">
          Please provide your contact details to complete your setup
        </Text>

        <FormProvider key="profile" {...profileForm}>
          <Stack pb={6}>
            <inputs.TextInput
              name="givenName"
              label="Given name"
              variant="small"
            />
            <inputs.TextInput
              name="familyName"
              label="Family name"
              variant="small"
            />
            <inputs.TextInput
              name="primaryContactNumber"
              label="Primary contact number"
              variant="small"
            />
          </Stack>
          <ButtonGroup w="100%" mt={3}>
            <Button
              type="submit"
              disabled={updateProfile.isUpdatingUserProfile}
              isLoading={updateProfile.isUpdatingUserProfile}
              onClick={profileForm.handleSubmit(async (data) => {
                await updateProfile.updateUserProfile({
                  givenName: data.givenName,
                  familyName: data.familyName,
                  primaryContactNumber: data.primaryContactNumber,
                });
              })}
            >
              Save and continue
            </Button>
            {onCancel && (
              <Button
                disabled={updateProfile.isUpdatingUserProfile}
                variant="outline"
                onClick={onCancel}
              >
                Cancel
              </Button>
            )}
          </ButtonGroup>
        </FormProvider>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {steps}
      <Heading variant="section">Register your Business</Heading>
      <Text pb={6} color="brand.dark-gray">
        If you are an employee of this business or it already exists contact the
        current owner.
      </Text>
      <FormProvider key="register" {...useFormMethods}>
        <form onSubmit={onSubmit}>
          <Stack spacing={2} data-testid="registerBusinessForm" w="100%">
            <Stack spacing={2}>
              <inputs.TextInput name="businessName" label="Business name" />
              <inputs.TextInput name="abn" label="ABN" />
              <Text fontSize="xs" mb={3} />
              <AcceptTOCCheckbox
                hasAcceptedTerms={hasAcceptedTerms}
                toggle={() => setHasAcceptedTerms(!hasAcceptedTerms)}
              />
            </Stack>
          </Stack>

          <ButtonGroup justifyContent="center" w="100%" mt={3}>
            <Button
              type="submit"
              disabled={isSubmitting || !hasAcceptedTerms}
              isLoading={isSubmitting}
            >
              Register
            </Button>
            {onCancel && (
              <Button
                disabled={isSubmitting}
                variant="outline"
                onClick={onCancel}
              >
                Cancel
              </Button>
            )}
          </ButtonGroup>
        </form>
      </FormProvider>
    </Stack>
  );
};
